<form autocomplete="off" class="flex flex-col gap-4" [formGroup]="form">
  <div class="query-section flex flex-col justify-between">
    <h3>Configuration</h3>
    <div class="flex flex-row justify-between">
      <div class="flex flex-row gap-4">
        <mat-form-field>
          <mat-label>Core network</mat-label>
          <mat-select formControlName="coreNetwork">
            <mat-option value="core">Core</mat-option>
            <mat-option value="noncore">Non-core</mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field>
          <mat-label>Expert target</mat-label>
          <input
            matInput
            type="number"
            step="1"
            min="1"
            formControlName="target"
            data-test-id="search-query-form-expert-target-input"
            [max]="targetMax"
          />
          @if (form.get('target')?.hasError('required')) {
            <mat-error> Target is required </mat-error>
          }
          @if (form.get('target')?.hasError('max')) {
            <mat-error> Target cannot exceed {{ targetMax | number }} </mat-error>
          }
        </mat-form-field>
      </div>

      <div class="flex flex-col gap-4">
        <button mat-button type="button" (click)="uploadQuery.emit()">Upload query</button>
        <button mat-button type="button" (click)="downloadQuery.emit()">Save query</button>
      </div>
    </div>
  </div>

  <div class="query-section flex flex-col gap-2 justify-between">
    <h3>Target profile</h3>

    <div class="flex flex-row flex-wrap justify-between gap-2">
      <div class="flex flex-row gap-2 flex-1">
        <ct-chip-list-control
          label="Countries*"
          formControlName="countries"
          data-test-id="search-query-form-countries-chip-list"
          placeholder="Add item..."
          optionDisplayPropKey="country"
          optionValuePropKey="abbreviation"
          class="flex-1"
          [uniqueValues]="true"
          [splitByPipe]="true"
          [options]="countries"
          [allowOptionsOnly]="true"
        >
        </ct-chip-list-control>
        <ct-chip-list-control
          label="States"
          formControlName="subdivisions"
          placeholder="Add item..."
          class="flex-1"
          [uniqueValues]="true"
          [splitByPipe]="true"
          [options]="subdivisions"
          [allowOptionsOnly]="true"
        >
        </ct-chip-list-control>
      </div>

      <div class="flex flex-row gap-2 flex-1">
        <div
          class="checkbox-bar flex-1 cursor-pointer"
          [class]="{ 'checkbox-bar-active': phoneNumberValue }"
          (click)="togglePhoneNumber()"
        >
          <span>
            <mat-checkbox #phoneCheckbox labelPosition="before" formControlName="phoneNumber">
              Must include phone number
            </mat-checkbox>
          </span>
        </div>
        <mat-form-field class="flex-1">
          <mat-label>{{ (excludeRoles.value || []).length ? 'Exclude roles' : 'No exclusions' }}</mat-label>
          <mat-select multiple [formControl]="excludeRoles">
            <mat-select-trigger>
              @if ((excludeRoles.value || []).length === exclusionOptions.length) {
                <span>All excluded</span>
              } @else {
                <span>{{ excludeRoles.value || [] | exclusionLabel }}</span>
              }
            </mat-select-trigger>
            @for (option of exclusionOptions; track option.value) {
              <mat-option [value]="option.value">{{ option.label }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
      </div>
    </div>

    <mat-divider class="pb-2"></mat-divider>

    <div class="flex flex-row gap-4">
      @for (field of ['names']; track field) {
        <ng-container
          [ngTemplateOutlet]="ConditionButton"
          [ngTemplateOutletContext]="{ field, controlArray: nameConditions }"
        >
        </ng-container>
      }
    </div>

    <div class="flex flex-col gap-2">
      @for (item of nameConditions.controls; track item) {
        <ng-container
          [ngTemplateOutlet]="ConditionArrayItem"
          [ngTemplateOutletContext]="{
            form,
            formArrayName: 'nameConditions',
            controlArray: nameConditions,
            item,
            index: $index,
            last: $last,
          }"
        >
        </ng-container>
      }
    </div>
  </div>

  <div class="query-section flex flex-col gap-2 justify-between">
    <h3>Company, role, and industry</h3>

    <div class="flex flex-row flex-wrap gap-4">
      <ng-container [ngTemplateOutlet]="JobHistoryConditionButton" [ngTemplateOutletContext]="{}"> </ng-container>

      @for (field of ['roles', 'affiliations', 'workSettings', 'industries']; track field) {
        <ng-container
          [ngTemplateOutlet]="ConditionButton"
          [ngTemplateOutletContext]="{ field, controlArray: workConditions }"
        >
        </ng-container>
      }
    </div>

    <div class="flex flex-col gap-2">
      @for (item of jobHistoryConditions.controls; track item) {
        <ng-container
          [ngTemplateOutlet]="JobHistoryConditionArrayItem"
          [ngTemplateOutletContext]="{
            form,
            formArrayName: 'jobHistoryConditions',
            controlArray: jobHistoryConditions,
            item,
            index: $index,
            last: $last,
          }"
        >
        </ng-container>
      }
    </div>

    @if (jobHistoryConditions.controls.length && workConditions.controls.length) {
      <div class="flex justify-center items-center">AND</div>
    }

    <div class="flex flex-col gap-2">
      @for (item of workConditions.controls; track item) {
        <ng-container
          [ngTemplateOutlet]="ConditionArrayItem"
          [ngTemplateOutletContext]="{
            form,
            formArrayName: 'workConditions',
            controlArray: workConditions,
            item,
            index: $index,
            last: $last,
          }"
        >
        </ng-container>
      }
    </div>
  </div>

  <div class="query-section flex flex-col gap-2 justify-between">
    <h3>Knowledge and expertise</h3>

    <div class="flex flex-row flex-wrap gap-4">
      @for (field of ['expertise', 'treatments', 'medicalConditions']; track field) {
        <ng-container
          [ngTemplateOutlet]="ConditionButton"
          [ngTemplateOutletContext]="{ field, controlArray: knowledgeConditions }"
        >
        </ng-container>
      }
      @for (field of ['publications', 'boardCertifications', 'clinicalTrials']; track field) {
        <ng-container [ngTemplateOutlet]="ConditionButtonDisabled" [ngTemplateOutletContext]="{ field }">
        </ng-container>
      }
    </div>

    <div class="flex flex-col gap-2">
      @for (item of knowledgeConditions.controls; track item) {
        <ng-container
          [ngTemplateOutlet]="ConditionArrayItem"
          [ngTemplateOutletContext]="{
            form,
            formArrayName: 'knowledgeConditions',
            controlArray: knowledgeConditions,
            item,
            index: $index,
            last: $last,
          }"
        >
        </ng-container>
      }
    </div>
  </div>
</form>

<ng-template #ConditionButton let-field="field" let-controlArray="controlArray">
  <div class="condition-button-group flex flex-row items-center gap-1 whitespace-nowrap">
    {{ field | searchQueryField }}
    <button
      matIconButton
      class="condition-icon-button"
      type="button"
      appearance="outline"
      [attr.data-test-id]="`search-query-form-${field}-include-condition`"
      (click)="addCondition(controlArray, field, 'include')"
    >
      <mat-icon>add</mat-icon>
    </button>
    <button
      matIconButton
      class="condition-icon-button"
      type="button"
      appearance="outline"
      [attr.data-test-id]="`search-query-form-${field}-exclude-condition`"
      (click)="addCondition(controlArray, field, 'exclude')"
    >
      <mat-icon>remove</mat-icon>
    </button>
  </div>
</ng-template>

<ng-template #ConditionButtonDisabled let-field="field">
  <div
    class="condition-button-group flex flex-row items-center gap-1 whitespace-nowrap"
    disabled
    style="color: lightgrey"
  >
    {{ field | searchQueryField }}
    <button
      matIconButton
      class="condition-icon-button"
      type="button"
      appearance="outline"
      disabled
      style="color: lightgrey"
    >
      <mat-icon>add</mat-icon>
    </button>
    <button
      matIconButton
      class="condition-icon-button"
      type="button"
      appearance="outline"
      disabled
      style="color: lightgrey"
    >
      <mat-icon>remove</mat-icon>
    </button>
  </div>
</ng-template>

<ng-template #JobHistoryConditionButton>
  <div class="condition-button-group flex flex-row items-center gap-1 whitespace-nowrap">
    Job history
    <button
      matIconButton
      class="condition-icon-button"
      type="button"
      appearance="outline"
      (click)="addJobHistoryCondition('include')"
    >
      <mat-icon>add</mat-icon>
    </button>
    <button
      matIconButton
      class="condition-icon-button"
      type="button"
      appearance="outline"
      disabled
      style="color: lightgrey"
      (click)="addJobHistoryCondition('exclude')"
    >
      <mat-icon>remove</mat-icon>
    </button>
  </div>
</ng-template>

<ng-template
  #ConditionArrayItem
  let-form="form"
  let-formArrayName="formArrayName"
  let-item="item"
  let-index="index"
  let-controlArray="controlArray"
  let-last="last"
>
  @let field = item.get('field').value;
  @let operator = item.get('operator').value;
  @let exclusionLabel =
    field === 'roles' && operator === 'exclude' ? getExclusionLabelForTerms(item.get('terms').value) : null;

  <mat-card [formGroup]="form" [attr.data-test-id]="`search-query-form-${field}-${operator}-condition`">
    <mat-card-header>
      <mat-card-subtitle>
        {{ field | searchQueryField: operator }}
        @if (exclusionLabel) {
          ({{ exclusionLabel }})
        }
      </mat-card-subtitle>
    </mat-card-header>
    <mat-card-content [formArrayName]="formArrayName">
      <div [formGroupName]="index">
        <div class="flex flex-row gap-4 justify-between items-stretch">
          <div class="grow">
            @if (field === 'workSettings') {
              <ct-chip-list-control
                label="Items"
                formControlName="terms"
                placeholder="Add item..."
                [attr.data-test-id]="`search-query-form-${field}-${operator}-condition-chip-list`"
                [uniqueValues]="true"
                [splitByPipe]="true"
                [options]="workSettingsOptions"
                [allowOptionsOnly]="true"
              >
              </ct-chip-list-control>
            } @else {
              <ct-chip-list-control
                label="Items"
                formControlName="terms"
                placeholder="Add item..."
                [attr.data-test-id]="`search-query-form-${field}-${operator}-condition-chip-list`"
                [uniqueValues]="true"
                [splitByPipe]="true"
                [optionsFunction]="getAutocompleteOptions(field)"
              >
              </ct-chip-list-control>
            }
          </div>

          <div class="flex flex-col gap-2 mt-0">
            <button
              matIconButton
              type="button"
              appearance="outline"
              matTooltip="Clear search terms"
              (click)="removeCondition(controlArray, index)"
            >
              <mat-icon>clear</mat-icon>
            </button>
            <button
              matIconButton
              type="button"
              appearance="outline"
              matTooltip="Copy search terms"
              (click)="copyCondition(controlArray, index)"
            >
              <mat-icon>content_copy</mat-icon>
            </button>
          </div>
        </div>

        @if (['roles', 'affiliations', 'industries'].includes(field)) {
          @let showPastOptions = field !== 'industries';

          <mat-radio-group formControlName="time">
            <div class="flex flex-col gap-2">
              <div class="flex flex-row justify-end items-center gap-4">
                <mat-radio-button value="all">All time</mat-radio-button>
                <mat-radio-button value="current">Current only</mat-radio-button>
                @if (showPastOptions) {
                  <mat-radio-button value="past">Past only</mat-radio-button>
                }
              </div>
              @if (showPastOptions) {
                <div class="flex flex-row justify-end items-center gap-4" [matTooltip]="pastAdvancedTooltip">
                  <mat-radio-button value="past-advanced">
                    <div class="flex flex-row justify-end items-center gap-2 text-sm">
                      <div class="whitespace-nowrap">Past only, ended</div>
                      <div class="w-24">
                        <mat-select formControlName="windowType">
                          <mat-option value="at-least">at least</mat-option>
                          <mat-option value="no-more-than">at most</mat-option>
                        </mat-select>
                      </div>
                      <input matInput type="number" step="1" min="0" formControlName="windowMonths" class="w-12" />
                      <div class="whitespace-nowrap">months ago</div>
                    </div>
                  </mat-radio-button>
                </div>
              }
            </div>
          </mat-radio-group>
        }
      </div>
    </mat-card-content>
  </mat-card>

  @if (!last) {
    <div class="flex justify-center items-center">AND</div>
  }
</ng-template>

<ng-template
  #JobHistoryConditionArrayItem
  let-form="form"
  let-formArrayName="formArrayName"
  let-item="item"
  let-index="index"
  let-controlArray="controlArray"
  let-last="last"
>
  <mat-card [formGroup]="form">
    <mat-card-header>
      <mat-card-subtitle> {{ item.get('operator').value | titlecase }} job history </mat-card-subtitle>
    </mat-card-header>
    <mat-card-content [formArrayName]="formArrayName">
      <div [formGroupName]="index">
        <div class="flex flex-row gap-4 justify-between items-stretch">
          <div class="grow">
            <ct-chip-list-control
              label="Roles"
              formControlName="roles"
              placeholder="Add item..."
              [uniqueValues]="true"
              [splitByPipe]="true"
              [optionsFunction]="getAutocompleteOptions('roles')"
            >
            </ct-chip-list-control>
          </div>
          <div class="grow">
            <ct-chip-list-control
              label="Organisations"
              formControlName="affiliations"
              placeholder="Add item..."
              [uniqueValues]="true"
              [splitByPipe]="true"
              [optionsFunction]="getAutocompleteOptions('affiliations')"
            >
            </ct-chip-list-control>
          </div>

          <div class="mt-2 flex flex-col gap-2">
            <button
              matIconButton
              type="button"
              appearance="outline"
              matTooltip="Clear search terms"
              (click)="removeCondition(controlArray, index)"
            >
              <mat-icon>clear</mat-icon>
            </button>
            <button
              matIconButton
              type="button"
              appearance="outline"
              matTooltip="Copy search terms"
              (click)="copyCondition(controlArray, index)"
            >
              <mat-icon>content_copy</mat-icon>
            </button>
          </div>
        </div>

        <mat-radio-group formControlName="time">
          <div class="flex flex-col gap-2">
            <div class="flex flex-row justify-end items-center gap-4">
              <mat-radio-button value="all">All time</mat-radio-button>
              <mat-radio-button value="current">Current only</mat-radio-button>
              <mat-radio-button value="past">Past only</mat-radio-button>
            </div>
            <div class="flex flex-row justify-end items-center gap-4" [matTooltip]="pastAdvancedTooltip">
              <mat-radio-button value="past-advanced">
                <div class="flex flex-row justify-end items-center gap-2 text-sm">
                  <div class="whitespace-nowrap">Past only, ended</div>
                  <div class="w-24">
                    <mat-select formControlName="windowType">
                      <mat-option value="at-least">at least</mat-option>
                      <mat-option value="no-more-than">at most</mat-option>
                    </mat-select>
                  </div>
                  <input matInput type="number" step="1" min="0" formControlName="windowMonths" class="w-12" />
                  <div class="whitespace-nowrap">months ago</div>
                </div>
              </mat-radio-button>
            </div>
          </div>
        </mat-radio-group>
      </div>
    </mat-card-content>
  </mat-card>

  @if (!last) {
    <div class="flex justify-center items-center">AND</div>
  }
</ng-template>
